import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Phone, Clock, BadgeCheck, Award, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { userAPI, analyticsAPI } from '../../lib/api';
import { toast } from 'sonner';

const ListingCard = ({ listing, onFavoriteToggle, isFavorite = false }) => {
  const { isAuthenticated } = useAuth();
  
  const {
    listing_id,
    slug,
    name,
    listing_type,
    description,
    city,
    state,
    phone,
    photos,
    rating_avg,
    review_count,
    verified,
    plan,
    categories,
    specialties,
    hours,
  } = listing;

  const defaultImage = {
    facility: 'https://images.pexels.com/photos/127873/pexels-photo-127873.jpeg',
    specialist: 'https://images.unsplash.com/photo-1645066928295-2506defde470?crop=entropy&cs=srgb&fm=jpg&q=85',
    pharmacy: 'https://images.pexels.com/photos/8657293/pexels-photo-8657293.jpeg',
  };

  const imageUrl = photos?.[0] || defaultImage[listing_type];

  const typeColors = {
    facility: 'bg-teal-100 text-teal-700',
    specialist: 'bg-blue-100 text-blue-700',
    pharmacy: 'bg-rose-100 text-rose-700',
  };

  const typeLabels = {
    facility: 'Facility',
    specialist: 'Specialist',
    pharmacy: 'Pharmacy',
  };

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please log in to save favorites');
      return;
    }
    
    try {
      if (isFavorite) {
        await userAPI.removeFavorite(listing_id);
        toast.success('Removed from favorites');
      } else {
        await userAPI.addFavorite(listing_id);
        toast.success('Added to favorites');
      }
      if (onFavoriteToggle) onFavoriteToggle(listing_id);
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleCardClick = async () => {
    try {
      await analyticsAPI.track({ listing_id, event_type: 'view' });
    } catch (error) {
      // Silent fail for analytics
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  // Check if open now (simplified)
  const isOpenNow = () => {
    if (!hours) return null;
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const today = days[new Date().getDay()];
    const todayHours = hours[today];
    if (!todayHours || todayHours === 'Closed') return false;
    if (todayHours === '24/7') return true;
    return true; // Simplified - would need proper time parsing
  };

  const openStatus = isOpenNow();

  return (
    <Link
      to={`/listing/${slug || listing_id}`}
      className="listing-card block group"
      onClick={handleCardClick}
      data-testid={`listing-card-${listing_id}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge className={typeColors[listing_type]}>
            {typeLabels[listing_type]}
          </Badge>
          {plan === 'premium' && (
            <Badge className="bg-purple-100 text-purple-700">
              <Award className="w-3 h-3 mr-1" />
              Featured
            </Badge>
          )}
        </div>
        <button
          onClick={handleFavoriteClick}
          className={`absolute top-3 right-3 p-2 rounded-full bg-white/80 backdrop-blur-sm transition-all
            ${isFavorite ? 'text-rose-500' : 'text-gray-400 hover:text-rose-500'}`}
          data-testid={`favorite-btn-${listing_id}`}
        >
          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Name & Verified */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading font-semibold text-lg text-gray-900 line-clamp-1 group-hover:text-teal-700 transition-colors">
            {name}
          </h3>
          {verified && (
            <BadgeCheck className="w-5 h-5 text-teal-600 shrink-0" />
          )}
        </div>

        {/* Categories/Specialties */}
        {(categories?.length > 0 || specialties?.length > 0) && (
          <p className="text-sm text-gray-500 mb-2 line-clamp-1">
            {[...(categories || []), ...(specialties || [])].slice(0, 2).join(' • ')}
          </p>
        )}

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          {renderStars(rating_avg)}
          <span className="text-sm font-medium text-gray-900">{rating_avg?.toFixed(1) || '0.0'}</span>
          <span className="text-sm text-gray-500">({review_count || 0} reviews)</span>
        </div>

        {/* Location & Status */}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span>{city}, {state}</span>
          </div>
          {openStatus !== null && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span className={openStatus ? 'text-green-600' : 'text-red-500'}>
                {openStatus ? 'Open' : 'Closed'}
              </span>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mt-4">
          {phone && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                analyticsAPI.track({ listing_id, event_type: 'call' });
                window.open(`tel:${phone}`, '_self');
              }}
            >
              <Phone className="w-3 h-3 mr-1" />
              Call
            </Button>
          )}
          <Button
            size="sm"
            className="flex-1 bg-teal-700 hover:bg-teal-800 text-white text-xs"
          >
            View Details
          </Button>
        </div>
      </div>
    </Link>
  );
};

export default ListingCard;
